import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, Newspaper, MessagesSquare, Linkedin, UserRound, PenLine } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/SectionHead";
import { PROFILES, SPEAKING, ARTICLES, INTERVIEWS } from "@/data/media";
import { useContent } from "@/lib/api";

const Ext = ({ href, children, className = "", testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}>{children}</a>
);

const GroupHead = ({ icon: I, title }) => (
  <h3 className="flex items-center gap-3 text-lg font-extrabold text-white">
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#7C3AED]"><I className="h-5 w-5" /></span>{title}
  </h3>
);

const FeatureCard = ({ it, testid }) => (
  <Ext href={it.url} testid={testid}
    className="group flex h-full flex-col justify-between rounded-[24px] border border-white/10 bg-white/5 p-6 transition-[transform,background-color] duration-500 hover:-translate-y-1 hover:bg-white/10">
    <p className="text-xs font-bold uppercase tracking-widest text-[#C4B5FD]">{it.where}</p>
    <p className="mt-3 text-lg font-extrabold leading-snug text-white">{it.title}</p>
    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#FF8A65]">Read more <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
  </Ext>
);

const Articles = () => (
  <ol className="mt-6 divide-y divide-white/10 border-y border-white/10">
    {ARTICLES.map(([t, u], i) => (
      <li key={u}>
        <Ext href={u} testid={`media-article-${i + 1}`} className="group flex items-center gap-4 py-4 text-violet-50 transition-colors duration-300 hover:text-white">
          <span className="serif-accent w-8 shrink-0 text-2xl text-[#C4B5FD]">{i + 1}</span>
          <span className="flex-1 text-base font-semibold">{t}</span>
          <ArrowUpRight className="h-4 w-4 shrink-0 text-[#FF8A65] transition-transform duration-300 group-hover:-translate-y-0.5" />
        </Ext>
      </li>
    ))}
  </ol>
);

// Freya's own articles from the blog CMS: the latest 8, in the same order as the blog page.
const BlogArticles = () => {
  const { items } = useContent("posts");
  const posts = Array.isArray(items) ? items.slice(0, 8) : [];
  if (!posts.length) return null;
  return (
    <Reveal className="mt-14" data-testid="about-media-blog">
      <GroupHead icon={PenLine} title="Latest from the Digital AdDoctor blog" />
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {posts.map((p, i) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`media-blog-${i + 1}`}
            className="group flex h-full flex-col justify-between rounded-[24px] border border-white/10 bg-white/5 p-6 transition-[transform,background-color] duration-500 hover:-translate-y-1 hover:bg-white/10">
            <p className="text-xs font-bold uppercase tracking-widest text-[#C4B5FD]">{p.category || "Blog"}</p>
            <p className="mt-3 text-base font-extrabold leading-snug text-white">{p.title}</p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#FF8A65]">Read article <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
          </Link>
        ))}
      </div>
      <Link to="/blog" className="btn-ghost-dark mt-6" data-testid="media-blog-all">Browse all {items.length} articles <ArrowUpRight className="h-4 w-4" /></Link>
    </Reveal>
  );
};

export const MediaSection = () => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-24 text-white sm:py-28" data-testid="about-media">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead dark eyebrow="Speaking, publications & media" title={<>Sharing what I&rsquo;ve <span className="serif-accent font-normal text-[#C4B5FD]">learned.</span></>}
        text="Talks, articles, interviews and practical guides on PPC, SEO, digital marketing and the psychology behind why people click, enquire and buy." />
      <div className="mt-14 grid gap-12 lg:grid-cols-2">
        <div className="space-y-12">
          <Reveal>
            <GroupHead icon={Mic} title="Speaking engagements" />
            <div className="mt-6 grid gap-4">{SPEAKING.map((it, i) => <FeatureCard key={it.url} it={it} testid={`media-speaking-${i + 1}`} />)}</div>
          </Reveal>
          <Reveal>
            <GroupHead icon={MessagesSquare} title="Interviews & features" />
            <div className="mt-6 grid gap-4 sm:grid-cols-2">{INTERVIEWS.map((it, i) => <FeatureCard key={it.url} it={it} testid={`media-interview-${i + 1}`} />)}</div>
          </Reveal>
        </div>
        <Reveal delay={0.08}>
          <GroupHead icon={Newspaper} title="Published articles, Entrepreneur Middle East" />
          <Articles />
        </Reveal>
      </div>
      <BlogArticles />
      <Reveal className="mt-14 flex flex-wrap gap-3 border-t border-white/10 pt-8">
        {PROFILES.map(([label, u]) => (
          <Ext key={u} href={u} className="btn-ghost-dark" testid={`media-profile-${label.split(" ")[0].toLowerCase()}`}>
            {label === "LinkedIn" ? <Linkedin className="h-4 w-4" /> : <UserRound className="h-4 w-4" />}{label}
          </Ext>
        ))}
      </Reveal>
    </div>
  </section>
);
